import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";

const HERO_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/51h5805z_ChatGPT%20Image%20Aug%2027%2C%202026%2C%2010_51_00%20PM.png";

export default function NewArrivalsPage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => {
      const news = (r.data || []).filter((p) => p.new_arrival);
      setProducts(news);
    });
  }, []);

  return (
    <div data-testid="new-arrivals-page" className="bg-[#ffffff] min-h-screen">
      {/* Full-width editorial hero — displayed uncropped */}
      <section
        data-testid="new-arrivals-hero"
        className="relative w-full bg-[#0a0a0a] pt-20"
      >
        <img
          src={HERO_IMAGE}
          alt="Boscage New Arrivals — Autumn 2026"
          className="block w-full h-auto object-contain"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#c9a84c]">
              Just landed
            </p>
            <h1
              data-testid="new-arrivals-title"
              className="mt-2 font-serif text-3xl md:text-5xl text-white leading-tight"
            >
              New Arrivals
            </h1>
            <p className="mt-2 text-[12px] md:text-[13px] tracking-[0.14em] text-white/75 max-w-xl">
              Autumn 2026 — a small, considered edit. Numbered, registered
              and covered by the Maison Renewal Program for life.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8">
        <nav
          data-testid="new-arrivals-breadcrumb"
          className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#000000]/55"
        >
          <Link to="/" className="hover:text-[#000000] transition-colors">
            Home
          </Link>
          <ChevronRight size={12} strokeWidth={1.2} className="opacity-60" />
          <span className="text-[#000000]">New Arrivals</span>
        </nav>
      </div>

      {/* Two-column LV-style grid — Autumn 2026 new arrivals */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12"
          data-testid="new-arrivals-grid"
        >
          {products.map((p) => (
            <NewArrivalCard key={p.product_id} p={p} />
          ))}
        </div>
      </section>
    </div>
  );
}

/* ─────────── Hover-swap product card (product-only ↔ model wearing).
   `comingSoon` products render as a non-clickable div with a Coming-Soon overlay. */
const COMING_SOON_HANDLES = ["suede-leather-jacket-tan-brown"];

const NewArrivalCard = ({ p }) => {
  const isComingSoon = COMING_SOON_HANDLES.includes(p.handle);

  const inner = (
    <>
      <div className="relative overflow-hidden bg-[#f5f5f5] aspect-[4/5]">
        <img
          src={p.image}
          alt={p.title}
          data-testid={`new-arrival-image-${p.product_id}`}
          className={`absolute inset-0 w-full h-full object-cover ${
            isComingSoon ? "" : "transition-opacity duration-500 ease-out group-hover:opacity-0"
          }`}
        />
        {!isComingSoon && (
          <img
            src={p.model_image || p.image}
            alt=""
            aria-hidden="true"
            data-testid={`new-arrival-hover-${p.product_id}`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
          />
        )}

        {isComingSoon && (
          <>
            {/* Dim the product so the overlay reads cleanly */}
            <div className="absolute inset-0 bg-white/55 backdrop-blur-[1.5px]" />
            <div
              data-testid="new-arrival-coming-soon-overlay"
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#000000]/60">
                Autumn 2026
              </p>
              <p className="mt-3 font-serif text-3xl md:text-4xl text-[#000000]">
                Coming Soon
              </p>
              <span className="mt-6 inline-block text-[9px] tracking-[0.32em] uppercase text-[#000000]/70 border border-[#000000]/30 px-4 py-2">
                Stay tuned
              </span>
            </div>
          </>
        )}

        {p.compare_at_price && !isComingSoon && (
          <span
            className="absolute top-4 left-4 z-10 text-[9px] tracking-[0.28em] uppercase text-[#000000] bg-[#ffffff] px-2.5 py-1"
            data-testid="new-arrival-badge"
          >
            New
          </span>
        )}
      </div>
      <div className="mt-5 px-1">
        <h3 className="font-serif text-[17px] md:text-[19px] text-[#000000] leading-tight">
          {p.title}
        </h3>
        <p className="text-[12px] text-[#000000]/55 mt-1 tracking-[0.02em]">
          {p.subtitle}
        </p>
        <div className="mt-3 flex items-baseline gap-2">
          {isComingSoon ? (
            <span className="text-[13px] text-[#000000]/40 tracking-[0.14em] uppercase">
              Coming soon
            </span>
          ) : (
            <>
              {p.compare_at_price && (
                <span className="text-[13px] text-[#000000]/40 line-through">
                  ${p.compare_at_price.toFixed(0)}
                </span>
              )}
              <span className="text-[13px] text-[#000000]">
                ${p.price.toFixed(0)}
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );

  if (isComingSoon) {
    return (
      <div
        data-testid={`new-arrival-card-${p.product_id}`}
        className="group block cursor-not-allowed select-none"
        aria-disabled="true"
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={`/product/${p.handle}`}
      data-testid={`new-arrival-card-${p.product_id}`}
      className="group block"
    >
      {inner}
    </Link>
  );
};
